import { logWarning } from '@core/logging'

import { GLOBAL_CONFIG_KEY } from './constants'
import { parseAllScriptsConfig, parseScriptConfig } from './parseScriptConfig'

/**
 * Collects script configuration from `data-fwparam_` attributes and query parameters in src URLs
 * from all scripts on the page and stores it in the global config. This function parses all scripts exactly once.
 *
 * IMPORTANT: This function needs to be placed in the entrypoint of the ES module to have
 * `import.meta.url` available. For regular scripts, document.currentScript is available
 * from any location.
 *
 * @param importMetaUrl Optional import.meta.url for ES modules where document.currentScript is null
 */
export function collectEntrypointConfig(importMetaUrl?: string) {
  if (typeof window === 'undefined') {
    return
  }

  // Parse all scripts with data-fwparam_ attributes exactly once
  let allScriptsConfig = {}
  try {
    allScriptsConfig = parseAllScriptsConfig()
  } catch (error) {
    logWarning('Error parsing all scripts config', error)
  }

  // Also parse the current script for backward compatibility
  // This ensures any non-fwparam_ attributes from the current script are still collected
  let currentScriptConfig = {}
  try {
    currentScriptConfig = parseScriptConfig(importMetaUrl)
  } catch (error) {
    // Silently fail if current script config cannot be parsed
    // This is expected for subsequent calls where parseAllScriptsConfig already ran
  }

  try {
    // Get existing global config or initialize empty object
    const existingConfig = window[GLOBAL_CONFIG_KEY] || {}

    // Merge configs with precedence: allScriptsConfig > currentScriptConfig > existingConfig
    // This ensures data-fwparam_ attributes from all scripts take highest precedence
    const mergedConfig = {
      ...existingConfig,
      ...allScriptsConfig,
      ...currentScriptConfig,
    }

    // Store the merged config back to the global config key
    window[GLOBAL_CONFIG_KEY] = mergedConfig
  } catch (error) {
    logWarning('Error collecting script config', error)
  }
}
